import { motion } from "framer-motion";
import {
  Phone, MapPin, Star, Check, MessageCircle, ArrowRight,
  Scissors, Activity, Footprints, HeartPulse, Baby, ShieldPlus, Ruler, Sparkles,
  Award, HandHeart, Building2, CalendarClock, Clock, Zap, Stethoscope, Dna, Users,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { OWNER, whatsappLink } from "@/lib/legalContent";
import { IMG } from "@/lib/clinicImages";
import Gallery from "@/components/Gallery";

const HERO_IMG = IMG.recepcion;
const ABOUT_1 = IMG.retrato;
const ABOUT_2 = IMG.quiropodia;
const ABOUT_3 = IMG.salaEspera;

const SERVICES = [
  { icon: Scissors, title: "Quiropodia", tag: "Cuidado básico y mantenimiento del pie", desc: "Corte y fresado de uñas, eliminación de durezas, callos y helomas. Una sesión esencial para mantener tus pies sanos." },
  { icon: ShieldPlus, title: "Uñas Encarnadas", tag: "Tratamiento conservador", desc: "Solución para las uñas encarnadas (onicocriptosis) mediante técnicas conservadoras, con asesoramiento quirúrgico cuando resulta necesario." },
  { icon: Activity, title: "Estudio Biomecánico", tag: "Análisis de la pisada", desc: "Estudio completo de la pisada en estática y dinámica para detectar y corregir alteraciones biomecánicas con plantillas personalizadas." },
  { icon: Ruler, title: "Plantillas Personalizadas", tag: "Adaptadas a tu pisada", desc: "Soluciones personalizadas para tu pisada: analizamos la forma en la que apoyas y caminas para ofrecerte una solución adaptada a las necesidades de tus pies, mejorando la comodidad, la estabilidad y el rendimiento en cada paso." },
  { icon: Footprints, title: "Podología Deportiva", tag: "Para deportistas activos", desc: "Prevención de lesiones, ampollas y sobrecargas, y estudio de la pisada para tu calzado deportivo." },
  { icon: HeartPulse, title: "Pie Diabético", tag: "Cuidado especializado", desc: "Revisión y cuidado especializado del pie diabético. Prevención de complicaciones y educación sanitaria." },
  { icon: Baby, title: "Podología Infantil", tag: "Cuidamos a los más pequeños", desc: "Detección temprana de alteraciones en el desarrollo del pie infantil, con trato cercano y adaptado." },
  { icon: Sparkles, title: "Papilomas (Verrugas)", tag: "Eliminación efectiva", desc: "Tratamiento de verrugas plantares con distintas técnicas según el caso, con los mejores resultados y mínima molestia." },
  { icon: Zap, title: "Tratamiento con Láser", tag: "Tecnología láser única en la zona", desc: "Láser para verrugas plantares (papilomas), onicomicosis (hongos de las uñas) y procesos de dolor e inflamación como la fascitis plantar. Somos la única clínica de la zona con esta tecnología." },
  { icon: Stethoscope, title: "Asesoramiento Quirúrgico", tag: "Valoración y orientación especializada", desc: "Estudiamos tu caso y te asesoramos sobre las opciones quirúrgicas más adecuadas, acompañándote y orientándote en cada paso del proceso." },
  { icon: Dna, title: "Test Epigenético", tag: "Salud y estilo de vida", desc: "Análisis de laboratorio que mide cómo los factores ambientales y el estilo de vida (dieta, estrés, ejercicio o exposición a toxinas) modifican la actividad de tus genes, sin alterar la secuencia del ADN." },
];

const VALUES = [
  { icon: Award, title: "Profesionalidad", desc: "Formación continua y técnicas actualizadas para ofrecerte el mejor tratamiento." },
  { icon: HandHeart, title: "Trato Cercano", desc: "Te escuchamos y entendemos tus necesidades para una atención personalizada." },
  { icon: Building2, title: "Equipo Moderno", desc: "Instalaciones renovadas y material de última generación para tu comodidad." },
  { icon: CalendarClock, title: "Horario Flexible", desc: "Adaptamos las citas a tu disponibilidad, mañana o tarde, de lunes a viernes." },
];

const TESTIMONIALS = [
  { text: "Lorena es una profesional excepcional. Llevaba años con problemas de uñas encarnadas y por fin he encontrado la solución. Trato impecable.", name: "María Jiménez", place: "El Viso del Alcor" },
  { text: "Las plantillas personalizadas me han cambiado la vida. Ya no tengo dolor de espalda al caminar. Muy recomendable.", name: "Antonio Pérez", place: "Mairena del Alcor" },
  { text: "Llevo a mi hija desde pequeña y siempre nos atienden de maravilla. Lorena es paciente, cariñosa y muy buena podóloga.", name: "Carmen Rodríguez", place: "Alcalá de Guadaíra" },
  { text: "Soy diabético y me reviso los pies cada poco tiempo. Confío plenamente en su criterio profesional. Cinco estrellas.", name: "José Luis Morales", place: "El Viso del Alcor" },
];

const FAQS = [
  { q: "¿Es necesario pedir cita previa?", a: "Sí, trabajamos con cita previa para poder dedicarte el tiempo que necesitas sin esperas. Puedes reservar fácilmente por WhatsApp o llamando por teléfono." },
  { q: "¿Cuánto dura una sesión de quiropodia?", a: "Una sesión de quiropodia suele durar entre 30 y 45 minutos, dependiendo del estado de los pies y del tratamiento necesario." },
  { q: "¿Trabajáis con seguros médicos?", a: "No trabajamos con ninguna compañía de seguros. Somos una clínica privada con precios ajustados y un trato totalmente personalizado." },
  { q: "¿Tenéis parking cerca?", a: "Sí, la clínica se encuentra en una zona con aparcamiento en la calle cercano, para que llegues cómodamente a tu cita." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-accent px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-primary">
    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
    {children}
  </span>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO */}
      <section id="inicio" className="hero-gradient pt-28 lg:pt-36">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:pb-24">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <Eyebrow>Clínica Podológica en El Viso del Alcor</Eyebrow>
            <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.02] text-foreground sm:text-6xl lg:text-7xl">
              Tus pies en las <span className="text-primary">mejores manos.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
              En la clínica de <strong className="text-foreground">{OWNER.brand}</strong> cuidamos la salud de tus pies
              con tratamientos personalizados, un trato cercano y la última tecnología en podología.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-lg"
              >
                Reservar cita por WhatsApp <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`tel:${OWNER.phoneBookingRaw}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-white px-7 py-3.5 text-sm font-bold text-foreground transition-colors hover:bg-secondary"
              >
                <Phone className="h-4 w-4 text-primary" /> {OWNER.phoneBooking}
              </a>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <div className="flex text-[#f5b100]">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <span className="text-sm text-muted-foreground">+200 pacientes nos avalan en El Viso del Alcor</span>
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fade} custom={1} className="relative">
            <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_80px_-30px_rgba(13,43,42,0.45)]">
              <img src={HERO_IMG} alt="Podóloga atendiendo a paciente en la clínica" className="h-[420px] w-full object-cover sm:h-[520px]" />
            </div>
            <div className="absolute right-4 top-4 flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
              <div className="flex -space-x-2">
                {["bg-primary", "bg-[#f5b100]", "bg-[#e07a5f]"].map((c, i) => (
                  <span key={i} className={`grid h-7 w-7 place-items-center rounded-full border-2 border-white text-[10px] font-bold text-white ${c}`}>{["L", "M", "A"][i]}</span>
                ))}
              </div>
              <div className="leading-tight">
                <p className="text-sm font-bold text-foreground">5.0 ★</p>
                <p className="text-[11px] text-muted-foreground">Valoración media</p>
              </div>
            </div>
            <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
              <MapPin className="h-5 w-5 text-primary" />
              <div className="leading-tight">
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">Visítanos</p>
                <p className="text-xs font-semibold text-foreground">{OWNER.addressShort}</p>
                <p className="text-[11px] text-muted-foreground">{OWNER.city}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section id="servicios" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow>Tratamientos</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-semibold text-foreground sm:text-5xl">Servicios de podología integral</h2>
          <p className="mt-4 text-muted-foreground">
            Tratamientos completos para el cuidado de tus pies: desde quiropodia básica hasta estudios biomecánicos y plantillas a medida.
          </p>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={fade} custom={i % 4}
              className="group rounded-2xl border border-border bg-white p-6 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl"
            >
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                <s.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-semibold text-foreground">{s.title}</h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-primary">{s.tag}</p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 overflow-hidden rounded-3xl bg-[#2a1233] px-6 py-10 text-center text-white sm:px-12">
          <h3 className="font-display text-3xl font-semibold">¿No sabes qué tratamiento necesitas?</h3>
          <p className="mx-auto mt-3 max-w-xl text-white/70">Pide una primera valoración. Te asesoramos sin compromiso.</p>
          <a
            href={whatsappLink("Hola Lorena, me gustaría hacer una primera valoración. ¿Cuándo tienes hueco?")}
            target="_blank" rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5"
          >
            Solicitar valoración <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* SOBRE MÍ */}
      <section id="sobre-mi" className="section-soft py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img src={ABOUT_1} alt="Lorena Cuevas, podóloga" className="col-span-1 h-72 w-full rounded-2xl object-cover object-top shadow-lg" />
              <img src={ABOUT_2} alt="Tratamiento de quiropodia" className="col-span-1 mt-10 h-72 w-full rounded-2xl object-cover shadow-lg" />
              <img src={ABOUT_3} alt="Sala de espera de la clínica" className="col-span-2 h-56 w-full rounded-2xl object-cover shadow-lg" />
            </div>
            <div className="absolute -bottom-5 left-6 rounded-2xl bg-primary px-6 py-4 text-white shadow-xl">
              <p className="font-display text-4xl font-bold leading-none">+10</p>
              <p className="text-xs font-semibold uppercase tracking-wide">Años cuidando pies en El Viso del Alcor</p>
            </div>
          </div>

          <div>
            <Eyebrow>Sobre la clínica</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-semibold text-foreground sm:text-5xl">Conoce a Lorena Cuevas</h2>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Apasionada por la salud del pie, Lorena dirige una clínica moderna y acogedora en el corazón de El Viso del
              Alcor. Su filosofía es clara: <strong className="text-foreground">tratar a cada paciente como único</strong>,
              con atención personalizada y un seguimiento cuidadoso.
            </p>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Combina experiencia, formación continua y la tecnología más avanzada para ofrecer tratamientos eficaces y
              duraderos. Desde un sencillo cuidado de uñas hasta plantillas a medida, tratamientos con láser o
              asesoramiento quirúrgico personalizado.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Más de 10 años de experiencia tratando todo tipo de patologías del pie",
                "Material esterilizado y protocolos sanitarios certificados",
                "Trato cercano, paciente y personalizado en cada visita",
                "Formación continua en las técnicas más avanzadas",
              ].map((t) => (
                <li key={t} className="flex gap-3 text-sm text-foreground/85">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-white"><Check className="h-3 w-3" /></span>
                  {t}
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-primary/20 bg-accent/60 p-5">
              <div className="flex items-center gap-2 text-primary">
                <Users className="h-5 w-5" />
                <p className="font-display text-xl font-semibold">Un equipo de 3 podólogas</p>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Estás en manos de <strong className="text-foreground">Lorena Cuevas</strong>,{" "}
                <strong className="text-foreground">Ángela Santos</strong> y{" "}
                <strong className="text-foreground">Laura Rodríguez</strong>: tres podólogas que cuidan de ti con la
                misma dedicación y profesionalidad.
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-20 grid max-w-7xl gap-6 px-5 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="rounded-2xl border border-border bg-white p-6">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary"><v.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display text-xl font-semibold text-foreground">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GALERÍA */}
      <Gallery />

      {/* OPINIONES */}
      <section id="opiniones" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow>Opiniones</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-semibold text-foreground sm:text-5xl">Lo que dicen nuestros pacientes</h2>
          <p className="mt-4 text-muted-foreground">La confianza y el cariño de nuestros pacientes es nuestro mayor reconocimiento.</p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure key={t.name} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i % 2}
              className="rounded-2xl border border-border bg-white p-7 shadow-sm">
              <div className="flex text-[#f5b100]">{[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}</div>
              <blockquote className="mt-4 font-display text-xl leading-relaxed text-foreground/90">“{t.text}”</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-accent font-bold text-primary">{t.name[0]}</span>
                <span>
                  <span className="block text-sm font-bold text-foreground">{t.name}</span>
                  <span className="block text-xs text-muted-foreground">{t.place}</span>
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-soft py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center">
            <Eyebrow>Preguntas frecuentes</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-semibold text-foreground sm:text-5xl">Resolvemos tus dudas</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-2xl border border-border bg-white px-5">
                <AccordionTrigger className="text-left font-display text-xl font-semibold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACTO */}
      <section id="contacto" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow>Contacto</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-semibold text-foreground sm:text-5xl">Reserva tu cita de forma sencilla</h2>
          <p className="mt-4 text-muted-foreground">
            Pide cita directamente por WhatsApp o llámanos. Estaremos encantados de atenderte y resolver cualquier duda.
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            <a href={`tel:${OWNER.phoneBookingRaw}`} className="flex items-center gap-4 rounded-2xl border border-border bg-white p-5 transition-colors hover:border-primary/30">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary"><Phone className="h-6 w-6" /></span>
              <span><span className="block text-sm font-bold text-foreground">Teléfono</span><span className="block text-sm text-muted-foreground">{OWNER.phoneBooking}</span></span>
            </a>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-2xl border border-border bg-white p-5 transition-colors hover:border-primary/30">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary"><MessageCircle className="h-6 w-6" /></span>
              <span><span className="block text-sm font-bold text-foreground">WhatsApp</span><span className="block text-sm text-muted-foreground">Reserva al instante</span></span>
            </a>
            <div className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-accent text-primary"><MapPin className="h-6 w-6" /></span>
              <span><span className="block text-sm font-bold text-foreground">Dirección</span><span className="block text-sm text-muted-foreground">{OWNER.addressShort}, {OWNER.city}</span></span>
            </div>
            <div className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-accent text-primary"><Clock className="h-6 w-6" /></span>
              <span>
                <span className="block text-sm font-bold text-foreground">Horario</span>
                <span className="block text-sm text-muted-foreground">Lun – Vie: 09:00–14:00 / 16:00–20:30</span>
                <span className="block text-sm text-muted-foreground">Mañanas y tardes · Sábado y domingo: cerrado</span>
              </span>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-white p-6 shadow-sm sm:p-8 lg:col-span-3">
            <h3 className="font-display text-2xl font-semibold text-foreground">Solicita tu cita</h3>
            <p className="mt-1 mb-5 text-sm text-muted-foreground">Rellena el formulario y te contactaremos por WhatsApp.</p>
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
